import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiBrain, FiBook, FiCheckCircle, FiClock, FiPhone, FiMessageCircle, FiAward, FiUsers } = FiIcons;

const AICoursesArticle = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - Optimized for local areas
    const seoData = {
      es: {
        title: 'Cursos de Inteligencia Artificial Murcia, La Manga y Cartagena | Formación Práctica IA',
        description: 'Cursos de IA en Murcia, La Manga del Mar Menor y Cartagena. Aprende ChatGPT, Midjourney, automatización. Formación práctica desde 99€. Individual y empresarial. ☎️ 642 643 499'
      },
      en: {
        title: 'AI Courses Murcia, La Manga & Cartagena | Practical AI Training',
        description: 'AI courses in Murcia, La Manga del Mar Menor and Cartagena. Learn ChatGPT, Midjourney, automation. Practical training from 99€. Individual and corporate. ☎️ 642 643 499'
      },
      it: {
        title: 'Corsi di Intelligenza Artificiale Murcia, La Manga e Cartagena | Formazione Pratica IA',
        description: 'Corsi di IA a Murcia, La Manga del Mar Menor e Cartagena. Impara ChatGPT, Midjourney, automazione. Formazione pratica da 99€. Individuale e aziendale. ☎️ 642 643 499'
      }
    };

    document.title = seoData[language].title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoData[language].description;

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = seoData[language].title;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = seoData[language].description;
  }, [language]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <SafeIcon icon={FiBrain} className="w-5 h-5" />
              <span className="font-semibold">{t('articles.courses.category')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('articles.courses.title')}
            </h1>
            <p className="text-xl text-purple-100 mb-8">
              {t('articles.courses.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{t('articles.readTime')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>{t('articles.updated')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8"
      >
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiBook} className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('articles.cta.courses')}</h3>
                <p className="text-sm text-gray-600">{t('articles.cta.learn')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>WhatsApp</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>{t('articles.cta.call')}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <img
            src="https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt={t('articles.courses.title')}
            className="w-full h-96 object-cover rounded-2xl mb-8 shadow-lg"
          />

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.courses.intro.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('articles.courses.intro.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('articles.courses.intro.p2')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.courses.modules.title')}
            </h2>
            <div className="space-y-6">
              {['module1', 'module2', 'module3', 'module4', 'module5'].map((key, index) => (
                <div key={key} className="bg-purple-50 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2 text-xl">
                        {t(`articles.courses.modules.${key}.title`)}
                      </h3>
                      <p className="text-gray-700 mb-3">
                        {t(`articles.courses.modules.${key}.description`)}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="w-4 h-4" />
                          <span>{t(`articles.courses.modules.${key}.duration`)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <SafeIcon icon={FiBook} className="w-4 h-4" />
                          <span>{t(`articles.courses.modules.${key}.level`)}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <SafeIcon icon={FiUsers} className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('articles.courses.who.title')}
              </h3>
              <ul className="space-y-3">
                {['target1', 'target2', 'target3', 'target4', 'target5'].map((key) => (
                  <li key={key} className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t(`articles.courses.who.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <SafeIcon icon={FiAward} className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('articles.courses.benefits.title')}
              </h3>
              <ul className="space-y-3">
                {['benefit1', 'benefit2', 'benefit3', 'benefit4', 'benefit5'].map((key) => (
                  <li key={key} className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t(`articles.courses.benefits.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.courses.tools.title')}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {['tool1', 'tool2', 'tool3', 'tool4', 'tool5', 'tool6'].map((key) => (
                <div key={key} className="bg-white rounded-xl p-6 text-center shadow">
                  <h4 className="font-bold text-gray-900 mb-2">
                    {t(`articles.courses.tools.${key}.name`)}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t(`articles.courses.tools.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.courses.format.title')}
            </h2>
            <div className="space-y-4">
              {['option1', 'option2', 'option3'].map((key) => (
                <div key={key} className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t(`articles.courses.format.${key}.title`)}
                  </h3>
                  <p className="text-gray-700">
                    {t(`articles.courses.format.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('articles.courses.finalCta.title')}
            </h2>
            <p className="text-xl mb-6 text-purple-100">
              {t('articles.courses.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499?text=Quiero%20informaci%C3%B3n%20sobre%20cursos%20de%20IA', '_blank')}
                className="flex items-center justify-center space-x-2 bg-green-600 text-white px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>{t('articles.courses.finalCta.whatsapp')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/#contact')}
                className="flex items-center justify-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiBrain} className="w-5 h-5" />
                <span>{t('articles.courses.finalCta.contact')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default AICoursesArticle;